import useAuth from "../../../hooks/useAuth";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { JsonEditor } from "jsoneditor-react";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import Ajv from "ajv";
import ace from "brace";
import { useTranslation } from "react-i18next";
import { VisualJsonBuilder, VisualJsonBuilderRef } from "./VisualJsonBuilder";
import {
  HiArrowRight,
  HiChevronDown,
  HiInformationCircle,
  HiOutlineExclamationCircle,
  HiOutlineViewGrid,
  HiViewGrid,
} from "react-icons/hi";
import "brace/mode/json";
import "brace/theme/github";
import "jsoneditor-react/es/editor.min.css";
import ConfirmModal from "../../public/ConfirmModal";
import { CustomButton } from "../../atoms/CustomButton";
import {
  useCreateProfileDataKey,
  useDeleteProfileDataKey,
  useGetProfileDataKeyValues,
  useRenameProfileData,
  useUpdateProfileDataKey,
} from "../../../hooks/users";
import { useGetUserProfileData } from "../../../hooks/entity";
import parse from "html-react-parser";
import { FolderHeader } from "../../atoms/FolderHeader";
import { FolderGrid, RESERVED_FOLDER_KEYS } from "../../atoms/FolderGrid";
import UpgradeModal from "../../modals/UpgradeModal";
import { PremiumBenefit } from "../../atoms/UpgradePromptContent";
import { useFolderFieldSpace } from "../../../hooks/useFolderFieldSpace";
import { NavigationHeader } from "../../atoms/NavigationHeader";
import { NewFolderForm } from "../../atoms/NewFolderForm";
import { KeyValueTable } from "../../atoms/KeyValueTable";
import { CreateFieldPrompt } from "../../atoms/CreateFieldPrompt";
import { usePermissions } from "../../../utils/permissions";
import { Cap, Level } from "../../../utils/rbacCatalog";

const ajv = new Ajv({ allErrors: true, verbose: true });

// Not system folders on the back-end: the AI engine must still discover agenda.next_start.
const AGENDA_FOLDER_KEYS = ["agenda", "agenda_availability"];
const isShownFolder = (key: string) => !AGENDA_FOLDER_KEYS.includes(key);

export const ControlledJsonEditor = ({
  value,
  onChange,
}: {
  value: any;
  onChange: (value: any) => void;
}) => {
  const jsonEditorRef = useRef<any>(null);

  const onResize = useCallback(() => {
    const editorInstance = jsonEditorRef?.current?.jsonEditor;
    if (editorInstance?.aceEditor) {
      editorInstance.aceEditor.resize();
    }
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => onResize());
    if (jsonEditorRef.current?.htmlElementRef instanceof HTMLElement) {
      resizeObserver.observe(jsonEditorRef.current.htmlElementRef);
    }
    return () => resizeObserver.disconnect();
  }, [onResize]);

  return (
    <JsonEditor
      ref={jsonEditorRef}
      value={value}
      onChange={onChange}
      schema={{}}
      mode="code"
      search={true}
      ajv={ajv}
      ace={ace}
      theme="ace/theme/github"
    />
  );
};

interface UserDetailsSpaceProps {
  entity_identification: string;
  customerId: string;
}

const UserDetailsSpace: React.FC<UserDetailsSpaceProps> = ({
  entity_identification,
  customerId,
}) => {
  const { t } = useTranslation();
  const { customer_identification, user, userEndedSubscription } = useAuth();
  const permissions = usePermissions(user);
  const isSelf = user.user_identification === customerId;
  const canWriteData = isSelf || permissions.can(Cap.USERS_DATA, Level.WRITE);
  const canDeleteData = isSelf || permissions.can(Cap.USERS_DATA, Level.DELETE);

  const visualJsonBuilderRef = useRef<VisualJsonBuilderRef>(null);
  const fetchingFoldersRef = useRef<Set<string>>(new Set());
  const [isHelpExpanded, setIsHelpExpanded] = useState(false);

  const {
    updatedData,
    setUpdatedData,
    isNewKeyProcess,
    setIsNewKeyProcess,
    selectOptionValue,
    setSelectOptionValue,
    showConfirm,
    setShowConfirm,
    editorMode,
    setEditorMode,
    currentView,
    setCurrentView,
    selectedFolder,
    setSelectedFolder,
    selectedField,
    setSelectedField,
    isNewFieldProcess,
    setIsNewFieldProcess,
    showDeleteFieldConfirm,
    setShowDeleteFieldConfirm,
    showFieldLimitModal,
    setShowFieldLimitModal,
    folderFieldCounts,
    setFolderFieldCounts,
    control,
    setValue,
    inputTextValue,
    tempFieldNameRef,
    originalFieldDataRef,
    maxLimit,
    targetUpgradeTier,
    largeBusinessTierUser,
    handleOpenFolder,
    handleBackToFolders,
    handleOpenField,
    handleBackToFolder,
    handleCreateNewFolder,
    handleCreateNewField,
    handleFieldChange,
    validateFolderName,
    cleanAndPrepareData,
    sanitizeInput,
    isReservedFolder,
  } = useFolderFieldSpace({
    user,
    reservedFolderKeys: RESERVED_FOLDER_KEYS,
    translationPrefix: "dashboard.UserDetailsSpace",
  });

  const { data, isLoading, isError, isSuccess, refetch } =
    useGetUserProfileData(
      customer_identification,
      user.entity_identification,
      customerId
    );
  const { mutate: getKeys, isSuccess: isSuccessGetKeys } =
    useGetProfileDataKeyValues();
  const { mutate: createKey } = useCreateProfileDataKey();
  const { mutate: updateKey, isLoading: isUpdating } =
    useUpdateProfileDataKey();
  const { mutate: deleteKey, isLoading: isDeleting } =
    useDeleteProfileDataKey();
  const { mutate: renameKey, isLoading: isRenaming } = useRenameProfileData();

  const fieldLimitBenefits: PremiumBenefit[] = [
    {
      id: "details-field-limit-premium-1",
      text: t("dashboard.upgrade.fieldLimit.benefit1") as string,
    },
    {
      id: "details-field-limit-premium-2",
      text: t("dashboard.upgrade.fieldLimit.benefit2") as string,
    },
    {
      id: "details-field-limit-premium-3",
      text: t("dashboard.upgrade.fieldLimit.benefit3") as string,
    },
  ];

  useEffect(() => {
    if (selectOptionValue !== "") {
      getKeys(
        {
          customer_identification,
          entityIdentification: user.entity_identification,
          customerId,
          key: selectOptionValue,
        },
        {
          onSuccess: (data, variables) => {
            const folderData =
              variables.key === ""
                ? {}
                : data?.data?.element?.user_data?.[variables.key] ?? {};
            setUpdatedData(folderData);
            setFolderFieldCounts((prev) => ({
              ...prev,
              [variables.key]: Object.keys(folderData).length,
            }));
          },
          onError: (err: AxiosError<any>) => {
            toast.error(err.response?.data?.message || err.message);
          },
        }
      );
    } else {
      setUpdatedData({});
    }
  }, [
    selectOptionValue,
    customer_identification,
    customerId,
    getKeys,
    user.entity_identification,
    setUpdatedData,
    setFolderFieldCounts,
  ]);

  useEffect(() => {
    if (
      currentView === "folders" &&
      data?.user_data &&
      data.user_data.length > 0
    ) {
      const foldersNeedingFetch = data.user_data.filter(
        (folderKey: string) =>
          isShownFolder(folderKey) &&
          folderFieldCounts[folderKey] === undefined &&
          !fetchingFoldersRef.current.has(folderKey)
      );

      if (foldersNeedingFetch.length === 0) return;

      foldersNeedingFetch.forEach((key: string) =>
        fetchingFoldersRef.current.add(key)
      );

      const fetchSequentially = async () => {
        const counts: Record<string, number> = {};

        for (const folderKey of foldersNeedingFetch) {
          try {
            const response: any = await new Promise((resolve, reject) => {
              getKeys(
                {
                  customer_identification,
                  entityIdentification: user.entity_identification,
                  customerId,
                  key: folderKey,
                },
                { onSuccess: resolve, onError: reject }
              );
            });

            const folderData =
              response?.data?.element?.user_data?.[folderKey] ?? {};
            counts[folderKey] = Object.keys(folderData).length;
          } catch (error) {
            console.error(`Error fetching folder "${folderKey}":`, error);
          }
        }

        setFolderFieldCounts((prev) => ({ ...prev, ...counts }));
        fetchingFoldersRef.current.clear();
      };

      fetchSequentially();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, data?.user_data]);

  const handleUpdateData = async (fieldData: Record<string, any>) => {
    const allFieldsData =
      currentView === "field" ? { ...updatedData, ...fieldData } : fieldData;
    const cleanedData = cleanAndPrepareData(allFieldsData);

    if (isNewKeyProcess && inputTextValue) {
      const existingFolders = data?.user_data || [];
      if (!validateFolderName(inputTextValue, existingFolders)) return;
    }

    const saveAndNavigate = async (key: string) => {
      return new Promise<void>((resolve, reject) => {
        const mutationFn = isNewKeyProcess ? createKey : updateKey;
        mutationFn(
          {
            customer_identification,
            entityIdentification: entity_identification,
            customerId,
            key,
            value: cleanedData,
          },
          {
            onSuccess: () => {
              tempFieldNameRef.current = "";
              originalFieldDataRef.current = {};

              if (key) {
                getKeys(
                  {
                    customer_identification,
                    entityIdentification: user.entity_identification,
                    customerId,
                    key,
                  },
                  {
                    onSuccess: (data, variables) => {
                      setUpdatedData(
                        variables === ""
                          ? {}
                          : data?.data?.element?.user_data[variables.key] ?? {}
                      );
                    },
                  }
                );
              }

              if (isNewKeyProcess) {
                setValue("newKeyName", "");
                setSelectOptionValue("");
                setSelectedFolder("");
                setIsNewKeyProcess(false);
                setUpdatedData({});
                setSelectedField("");
                setIsNewFieldProcess(false);
                setCurrentView("folders");
                setEditorMode("simple");
                toast.success(
                  t("dashboard.UserDetailsSpace.folderCreatedSuccess")
                );
                refetch();
              } else if (currentView === "field") {
                handleBackToFolder();
                toast.success(
                  t("dashboard.UserDetailsSpace.fieldSavedSuccess")
                );
              } else {
                setValue("newKeyName", "");
                setSelectOptionValue("");
                setIsNewKeyProcess(false);
                handleBackToFolders();
                toast.success(
                  t("dashboard.UserDetailsSpace.folderUpdatedSuccess")
                );
              }
              resolve();
            },
            onError: (error) => reject(error),
          }
        );
      });
    };

    const key = isNewKeyProcess ? inputTextValue : selectOptionValue;
    await saveAndNavigate(key);
  };

  const handleDeleteFolder = () => {
    deleteKey(
      {
        customer_identification,
        entityIdentification: entity_identification,
        customerId,
        key: selectOptionValue,
      },
      {
        onSuccess: () => {
          setValue("newKeyName", "");
          setSelectOptionValue("");
          setIsNewKeyProcess(false);
          setUpdatedData({});
          setShowConfirm(false);
          handleBackToFolders();
        },
      }
    );
  };

  const handleDeleteField = () => {
    if (!selectedField || !selectOptionValue) return;

    const newData = { ...updatedData };
    delete newData[selectedField];

    updateKey(
      {
        customer_identification,
        entityIdentification: entity_identification,
        customerId,
        key: selectOptionValue,
        value: newData,
      },
      {
        onSuccess: () => {
          setUpdatedData(newData);
          originalFieldDataRef.current = {};
          setShowDeleteFieldConfirm(false);
          setSelectedField("");
          setCurrentView("folder");
          setIsNewFieldProcess(false);
        },
        onError: () => setShowDeleteFieldConfirm(false),
      }
    );
  };

  const onUpdateFolderName = (newFolderName: string) => {
    const existingFolders = data?.user_data || [];
    if (!validateFolderName(newFolderName, existingFolders)) return;

    renameKey(
      {
        customer_identification,
        entityIdentification: entity_identification,
        customerId,
        new_key: newFolderName,
        value: updatedData,
        old_key: selectedFolder,
      },
      {
        onSuccess: () => {
          setSelectedFolder(newFolderName);
          setSelectOptionValue(newFolderName);
          toast.success(
            t("dashboard.UserDetailsSpace.folderRenamedSuccess", {
              folderName: newFolderName,
            })
          );
        },
        onError: () => {
          toast.error(t("dashboard.UserDetailsSpace.folderRenameFailed"));
        },
      }
    );
  };

  const shownFolders: string[] = (data?.user_data || []).filter(isShownFolder);

  let content;

  if (isLoading) {
    content = <p>{t("dashboard.UserDetailsSpace.loading")}</p>;
  }

  if (isError) {
    content = (
      <CustomButton
        onClick={() => refetch()}
        variant="primary"
        title={t("dashboard.UserDetailsSpace.btn-01") as string}
      />
    );
  }

  if (isSuccess) {
    content = (
      <React.Fragment>
        {currentView === "folders" ? (
          <FolderGrid
            folders={shownFolders}
            folderFieldCounts={folderFieldCounts}
            onOpenFolder={handleOpenFolder}
            onDeleteFolder={(folderKey) => {
              if (!canDeleteData) return;
              setSelectOptionValue(folderKey);
              setSelectedFolder(folderKey);
              setShowConfirm(true);
            }}
            onCreateFolder={() => {
              if (!canWriteData) return;
              handleCreateNewFolder(shownFolders);
            }}
            canCreate={canWriteData}
            canDelete={canDeleteData}
            showFieldCount={true}
          />
        ) : currentView === "folder" ? (
          <div className="space-y-4">
            <NavigationHeader
              onBack={handleBackToFolders}
              backLabel={
                t("dashboard.UserDetailsSpace.backToFolders") as string
              }
              showDelete={
                canDeleteData &&
                !isNewKeyProcess &&
                !!selectedFolder &&
                !isReservedFolder(selectedFolder)
              }
              onDelete={() => setShowConfirm(true)}
              deleteLabel={
                t("dashboard.UserDetailsSpace.deleteFolder") as string
              }
            />

            {isNewKeyProcess ? (
              <NewFolderForm
                control={control}
                value={inputTextValue}
                onValueChange={(value) =>
                  setValue("newKeyName", sanitizeInput(value))
                }
                title={
                  t("dashboard.UserDetailsSpace.createNewFolder") as string
                }
                label={t("dashboard.UserDetailsSpace.folderName") as string}
                placeholder={
                  t("dashboard.UserDetailsSpace.createPlaceholder") as string
                }
              />
            ) : (
              <FolderHeader
                folderName={selectedFolder}
                onUpdate={
                  isReservedFolder(selectedFolder) || !canWriteData
                    ? undefined
                    : onUpdateFolderName
                }
                isUpdating={isUpdating || isDeleting || isRenaming}
              />
            )}

            {selectOptionValue && isSuccessGetKeys && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                <KeyValueTable
                  fields={updatedData}
                  onEditField={handleOpenField}
                  onDeleteField={(fieldKey) => {
                    if (!canWriteData) return;
                    setSelectedField(fieldKey);
                    setShowDeleteFieldConfirm(true);
                  }}
                  onCreateField={() => {
                    if (!canWriteData) return;
                    handleCreateNewField(data?.user_data);
                  }}
                  maxLimit={maxLimit}
                  isLargeBusinessTier={largeBusinessTierUser}
                  isReadOnlyFolder={
                    isReservedFolder(selectedFolder) || !canWriteData
                  }
                  readOnlyTooltip={
                    t("dashboard.FolderGrid.reservedFolder") as string
                  }
                  onUpgradeClick={() => setShowFieldLimitModal(true)}
                />
              </div>
            )}

            {isNewKeyProcess && inputTextValue && canWriteData && (
              <CreateFieldPrompt
                onCreateField={() => handleCreateNewField(data?.user_data)}
                title={t("dashboard.UserDetailsSpace.addField") as string}
                subtitle={
                  t("dashboard.UserDetailsSpace.createFirstField") as string
                }
              />
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <NavigationHeader
              onBack={handleBackToFolder}
              backLabel={t("dashboard.UserDetailsSpace.backToFields") as string}
              showDelete={
                canWriteData &&
                !isNewFieldProcess &&
                !!selectedField &&
                !isReservedFolder(selectedFolder)
              }
              onDelete={() => setShowDeleteFieldConfirm(true)}
              deleteLabel={
                t("dashboard.UserDetailsSpace.deleteField") as string
              }
            />

            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 text-lg mb-4">
                {isNewFieldProcess ? (
                  t("dashboard.UserDetailsSpace.createNewField")
                ) : (
                  <>{t("dashboard.UserDetailsSpace.editField")}</>
                )}
              </h3>

              <div translate="no">
                {editorMode === "simple" ? (
                  <VisualJsonBuilder
                    ref={visualJsonBuilderRef}
                    initialValue={
                      isNewFieldProcess
                        ? { "": "" }
                        : { [selectedField]: updatedData[selectedField] }
                    }
                    onChange={(newValue) =>
                      handleFieldChange(newValue, isNewFieldProcess)
                    }
                    mode={editorMode}
                    onModeChange={setEditorMode}
                    allowedTypes={["text", "number", "boolean", "date"]}
                    onSave={handleUpdateData}
                    onCancel={handleBackToFolder}
                    isSaving={isUpdating}
                    userEndedSubscription={userEndedSubscription}
                    showActionButtons={true}
                    readOnlyFieldName={isReservedFolder(selectedFolder)}
                  />
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-end">
                      <button
                        onClick={() => setEditorMode("simple")}
                        className="group flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-all duration-200"
                      >
                        <HiViewGrid className="w-4 h-4 transition-transform group-hover:scale-110" />
                        <span>
                          {t("dashboard.UserDetailsSpace.switchToSimpleView")}
                        </span>
                        <HiArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                      </button>
                    </div>
                    <ControlledJsonEditor
                      value={
                        isNewFieldProcess
                          ? { "": "" }
                          : { [selectedField]: updatedData[selectedField] }
                      }
                      onChange={(newValue) =>
                        handleFieldChange(newValue, isNewFieldProcess)
                      }
                    />
                    <div className="flex justify-end mt-4 gap-2">
                      <button
                        onClick={handleBackToFolder}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                      >
                        {t("dashboard.UserDetailsSpace.key3")}
                      </button>
                      <CustomButton
                        onClick={() => {
                          if (!canWriteData) return;
                          const jsonObject = isNewFieldProcess
                            ? { "": "" }
                            : { [selectedField]: updatedData[selectedField] };
                          handleUpdateData(jsonObject);
                        }}
                        disabled={
                          isUpdating ||
                          userEndedSubscription ||
                          !updatedData ||
                          !canWriteData
                        }
                        isLoading={isUpdating}
                        variant="primary"
                        icon="save"
                        title={t("common.saveShort")}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <ConfirmModal
          cancelBtnName={t("dashboard.UserDetailsSpace.key3")}
          cancelBtnFn={() => setShowConfirm(false)}
          onClose={() => setShowConfirm(false)}
          confirmBtnFn={handleDeleteFolder}
          show={showConfirm}
          isLoading={isDeleting}
          ConfirmBtnColor="red"
          confirmBtnName={t("dashboard.UserDetailsSpace.key1")}
          title={t("dashboard.UserDetailsSpace.key6", {
            folderName:
              selectedFolder.length > 20
                ? selectedFolder.slice(0, 20) + "..."
                : selectedFolder,
          })}
          description={t("dashboard.UserDetailsSpace.key7")}
          icon={
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
          }
          iconStyle="text-center"
        />

        <ConfirmModal
          cancelBtnName={t("dashboard.UserDetailsSpace.key3")}
          cancelBtnFn={() => setShowDeleteFieldConfirm(false)}
          onClose={() => setShowDeleteFieldConfirm(false)}
          confirmBtnFn={handleDeleteField}
          show={showDeleteFieldConfirm}
          isLoading={isUpdating}
          ConfirmBtnColor="red"
          confirmBtnName={t("dashboard.UserDetailsSpace.key1")}
          title={t("dashboard.UserDetailsSpace.deleteField", {
            fieldName:
              selectedField.length > 20
                ? selectedField.slice(0, 20) + "..."
                : selectedField,
          })}
          description={t("dashboard.UserDetailsSpace.deleteFieldConfirm")}
          icon={
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
          }
          iconStyle="text-center"
        />

        <UpgradeModal
          showModal={showFieldLimitModal}
          setShowModal={setShowFieldLimitModal}
          targetPlan={targetUpgradeTier}
          title={t("dashboard.upgrade.fieldLimit.premiumTitle") as string}
          description={
            t("dashboard.upgrade.fieldLimit.premiumDescription") as string
          }
          benefits={fieldLimitBenefits}
          icon={HiOutlineViewGrid}
          iconTheme="blue"
          upgradeText={t("dashboard.upgrade.key3") as string}
        />
      </React.Fragment>
    );
  }

  return (
    <div className="w-full edit-preferences p-0">
      <div className="mb-4">
        <div className="mt-3 border border-blue-200 rounded-lg overflow-hidden bg-blue-50">
          <button
            type="button"
            onClick={() => setIsHelpExpanded((v) => !v)}
            className="w-full flex items-center justify-between p-3 hover:bg-blue-100 transition-colors"
            aria-expanded={isHelpExpanded}
          >
            <div className="flex items-center gap-2 text-left">
              <HiInformationCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-blue-900">
                {t("dashboard.UserDetailsSpace.help-title")}
              </h3>
            </div>
            <HiChevronDown
              className={`text-blue-600 transition-transform flex-shrink-0 ${
                isHelpExpanded ? "rotate-180" : ""
              }`}
              size={20}
            />
          </button>
          {isHelpExpanded && (
            <div className="p-3 pt-0 border-t border-blue-200">
              <p className="text-sm text-blue-900 whitespace-pre-line pt-3">
                {parse(t("dashboard.UserDetailsSpace.help-text"))}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="block relative">{content}</div>
    </div>
  );
};

export default UserDetailsSpace;
